/**
 * 脑波数据模拟器
 * 用于生成实时脑波数据，支持多患者、多波段、自然波动
 */
class BrainwaveSimulator {
  constructor() {
    // 患者数据存储
    this.patients = new Map();
    
    // 脑波波段配置
    this.waveBands = {
      delta: { min: 0.5, max: 4, baseWeight: 0.25 },    // δ波 (0.5-4Hz)
      theta: { min: 4, max: 8, baseWeight: 0.20 },      // θ波 (4-8Hz)  
      alpha: { min: 8, max: 13, baseWeight: 0.25 },     // α波 (8-13Hz)
      beta: { min: 13, max: 30, baseWeight: 0.20 },     // β波 (13-30Hz)
      gamma: { min: 30, max: 100, baseWeight: 0.10 }    // γ波 (30-100Hz)
    };
    
    // 睡眠状态对应的脑波模式
    this.sleepStatePatterns = {
      'awake': {
        delta: 0.05, theta: 0.10, alpha: 0.35, beta: 0.40, gamma: 0.10
      },
      'drowsy': {
        delta: 0.15, theta: 0.25, alpha: 0.30, beta: 0.25, gamma: 0.05
      },
      'light_sleep': {
        delta: 0.30, theta: 0.35, alpha: 0.20, beta: 0.10, gamma: 0.05
      },
      'deep_sleep': {
        delta: 0.60, theta: 0.25, alpha: 0.10, beta: 0.03, gamma: 0.02
      },
      'rem_sleep': {
        delta: 0.10, theta: 0.40, alpha: 0.15, beta: 0.25, gamma: 0.10
      }
    };
    
    // 更新间隔配置（3-5秒）
    this.updateIntervals = {
      min: 3000,  // 3秒
      max: 5000   // 5秒
    };
    
    // 启动模拟器
    this.isRunning = false;
    this.updateTimers = new Map();
  }

  /**
   * 初始化患者数据
   * @param {string} patientId - 患者ID
   * @param {string} sleepState - 初始睡眠状态
   * @param {string} treatmentStatus - 治疗状态 ('治疗中', '已结束', '治疗未开始')
   */
  initPatient(patientId, sleepState = 'light_sleep', treatmentStatus = '治疗中') {
    if (this.patients.has(patientId)) {
      return;
    }

    const patient = {
      id: patientId,
      sleepState: sleepState,
      treatmentStatus: treatmentStatus,
      lastUpdate: Date.now(),
      currentValues: this.generateInitialValues(sleepState),
      targetValues: null,
      transitionProgress: 0,
      transitionDuration: 2000, // 2秒过渡时间
      history: [],
      isCompleted: treatmentStatus === '已结束' // 标记是否已完成治疗
    };

    this.patients.set(patientId, patient);
    
    // 只有治疗中的患者才启动数据更新
    if (treatmentStatus === '治疗中') {
      this.startPatientUpdates(patientId);
    }
  }

  /**
   * 生成初始脑波值
   * @param {string} sleepState - 睡眠状态
   * @returns {Object} 脑波值对象
   */
  generateInitialValues(sleepState) {
    const pattern = this.sleepStatePatterns[sleepState] || this.sleepStatePatterns['light_sleep'];
    const values = {};
    
    // 生成基础值
    Object.keys(this.waveBands).forEach(band => {
      const baseValue = pattern[band] * 100;
      const variation = (Math.random() - 0.5) * 10; // ±5%的随机变化
      values[band] = Math.max(0, Math.min(100, baseValue + variation));
    });
    
    // 确保总和为100%
    return this.normalizeValues(values);
  }

  /**
   * 标准化脑波值，确保总和为100%
   * @param {Object} values - 原始脑波值
   * @returns {Object} 标准化后的脑波值
   */
  normalizeValues(values) {
    const total = Object.values(values).reduce((sum, val) => sum + val, 0);
    const normalized = {};
    
    if (total === 0) {
      // 如果总和为0，使用默认分布
      return {
        delta: 25, theta: 20, alpha: 25, beta: 20, gamma: 10
      };
    }
    
    Object.keys(values).forEach(band => {
      normalized[band] = Math.round((values[band] / total) * 100 * 100) / 100; // 保留2位小数
    });
    
    // 处理舍入误差，确保总和精确为100
    const normalizedTotal = Object.values(normalized).reduce((sum, val) => sum + val, 0);
    const diff = 100 - normalizedTotal;
    
    if (Math.abs(diff) > 0.01) {
      // 将差值分配给最大的波段
      const maxBand = Object.keys(normalized).reduce((a, b) => 
        normalized[a] > normalized[b] ? a : b
      );
      normalized[maxBand] = Math.round((normalized[maxBand] + diff) * 100) / 100;
    }
    
    return normalized;
  }

  /**
   * 生成自然波动的新目标值
   * @param {Object} currentValues - 当前脑波值
   * @param {string} sleepState - 睡眠状态
   * @returns {Object} 新的目标值
   */
  generateTargetValues(currentValues, sleepState) {
    const pattern = this.sleepStatePatterns[sleepState] || this.sleepStatePatterns['light_sleep'];
    const newValues = {};
    
    Object.keys(this.waveBands).forEach(band => {
      const patternValue = pattern[band] * 100;
      const currentValue = currentValues[band];
      
      // 计算趋向模式值的变化，但保持自然波动
      const trendFactor = 0.3; // 30%趋向模式值
      const randomFactor = 0.7; // 70%随机波动
      
      const trendValue = currentValue + (patternValue - currentValue) * trendFactor * 0.1;
      const randomVariation = (Math.random() - 0.5) * 8; // ±4%的随机变化
      
      newValues[band] = Math.max(0, Math.min(100, 
        trendValue * randomFactor + patternValue * (1 - randomFactor) + randomVariation
      ));
    });
    
    return this.normalizeValues(newValues);
  }

  /**
   * 启动患者数据更新
   * @param {string} patientId - 患者ID
   */
  startPatientUpdates(patientId) {
    if (this.updateTimers.has(patientId)) {
      clearTimeout(this.updateTimers.get(patientId));
    }

    const scheduleNextUpdate = () => {
      // 随机间隔3-5秒
      const interval = Math.random() * (this.updateIntervals.max - this.updateIntervals.min) + this.updateIntervals.min;
      
      const timer = setTimeout(() => {
        this.updatePatientData(patientId);
        scheduleNextUpdate(); // 递归调度下次更新
      }, interval);
      
      this.updateTimers.set(patientId, timer);
    };

    scheduleNextUpdate();
  }

  /**
   * 更新患者脑波数据
   * @param {string} patientId - 患者ID
   */
  updatePatientData(patientId) {
    const patient = this.patients.get(patientId);
    if (!patient) return;

    // 如果治疗已结束，不更新数据，保持固定值
    if (patient.isCompleted || patient.treatmentStatus === '已结束') {
      return;
    }

    const now = Date.now();
    
    // 如果没有目标值或已完成过渡，生成新的目标值
    if (!patient.targetValues || patient.transitionProgress >= 1) {
      patient.targetValues = this.generateTargetValues(patient.currentValues, patient.sleepState);
      patient.transitionProgress = 0;
      patient.transitionStartTime = now;
    }
    
    // 计算过渡进度
    const elapsed = now - (patient.transitionStartTime || now);
    patient.transitionProgress = Math.min(1, elapsed / patient.transitionDuration);
    
    // 使用缓动函数实现平滑过渡
    const easeProgress = this.easeInOutCubic(patient.transitionProgress);
    
    // 插值计算当前值
    const newValues = {};
    Object.keys(this.waveBands).forEach(band => {
      const current = patient.currentValues[band];
      const target = patient.targetValues[band];
      newValues[band] = current + (target - current) * easeProgress;
    });
    
    // 更新患者数据
    patient.currentValues = this.normalizeValues(newValues);
    patient.lastUpdate = now;
    
    // 保存历史数据（最多保留100条）
    patient.history.push({
      timestamp: now,
      values: { ...patient.currentValues },
      sleepState: patient.sleepState
    });
    
    if (patient.history.length > 100) {
      patient.history.shift();
    }
    
    logger.debug(`患者 ${patientId} 脑波数据更新:`, {
      sleepState: patient.sleepState,
      values: patient.currentValues,
      progress: Math.round(patient.transitionProgress * 100) + '%'
    });
  }

  /**
   * 缓动函数 - 三次方缓入缓出
   * @param {number} t - 进度值 (0-1)
   * @returns {number} 缓动后的值
   */
  easeInOutCubic(t) {
    return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
  }

  /**
   * 获取患者当前脑波数据
   * @param {string} patientId - 患者ID
   * @returns {Object|null} 脑波数据
   */
  getPatientData(patientId) {
    const patient = this.patients.get(patientId);
    if (!patient) return null;

    return {
      patientId: patient.id,
      timestamp: patient.lastUpdate,
      sleepState: patient.sleepState,
      brainwaves: {
        delta: Math.round(patient.currentValues.delta * 100) / 100,
        theta: Math.round(patient.currentValues.theta * 100) / 100,
        alpha: Math.round(patient.currentValues.alpha * 100) / 100,
        beta: Math.round(patient.currentValues.beta * 100) / 100,
        gamma: Math.round(patient.currentValues.gamma * 100) / 100
      },
      // 用于前端图表的频率带数据
      frequency_bands: {
        delta: patient.currentValues.delta / 100,
        theta: patient.currentValues.theta / 100,
        alpha: patient.currentValues.alpha / 100,
        beta: patient.currentValues.beta / 100,
        gamma: patient.currentValues.gamma / 100
      }
    };
  }

  /**
   * 获取所有患者数据
   * @returns {Array} 所有患者的脑波数据
   */
  getAllPatientsData() {
    const allData = [];
    this.patients.forEach((patient, patientId) => {
      allData.push(this.getPatientData(patientId));
    });
    return allData;
  }

  /**
   * 更新患者睡眠状态
   * @param {string} patientId - 患者ID
   * @param {string} newSleepState - 新的睡眠状态
   */
  updatePatientSleepState(patientId, newSleepState) {
    const patient = this.patients.get(patientId);
    if (!patient) return;

    // 如果治疗已结束，不更新睡眠状态，保持固定值
    if (patient.isCompleted || patient.treatmentStatus === '已结束') {
      logger.debug(`患者 ${patientId} 治疗已结束，睡眠状态保持固定`);
      return;
    }

    patient.sleepState = newSleepState;
    // 重置过渡进度，让脑波值逐渐适应新的睡眠状态
    patient.targetValues = null;
    patient.transitionProgress = 0;
    
    logger.debug(`患者 ${patientId} 睡眠状态更新为: ${newSleepState}`);
  }

  /**
   * 更新患者治疗状态
   * @param {string} patientId - 患者ID
   * @param {string} treatmentStatus - 治疗状态
   */
  updatePatientTreatmentStatus(patientId, treatmentStatus) {
    const patient = this.patients.get(patientId);
    if (!patient) return;

    patient.treatmentStatus = treatmentStatus;
    patient.isCompleted = treatmentStatus === '已结束';
    
    // 如果治疗结束，停止数据更新定时器
    if (treatmentStatus === '已结束') {
      const timer = this.updateTimers.get(patientId);
      if (timer) {
        clearTimeout(timer);
        this.updateTimers.delete(patientId);
      }
      logger.debug(`患者 ${patientId} 治疗已结束，数据更新已停止`);
    } else if (treatmentStatus === '治疗中' && !this.updateTimers.has(patientId)) {
      // 如果治疗重新开始，启动数据更新
      this.startPatientUpdates(patientId);
      logger.debug(`患者 ${patientId} 治疗重新开始，数据更新已启动`);
    }
  }

  /**
   * 移除患者
   * @param {string} patientId - 患者ID
   */
  removePatient(patientId) {
    if (this.updateTimers.has(patientId)) {
      clearTimeout(this.updateTimers.get(patientId));
      this.updateTimers.delete(patientId);
    }
    this.patients.delete(patientId);
    logger.debug(`患者 ${patientId} 已从模拟器中移除`);
  }

  /**
   * 启动模拟器
   */
  start() {
    this.isRunning = true;
    logger.debug('脑波数据模拟器已启动');
  }

  /**
   * 停止模拟器
   */
  stop() {
    this.isRunning = false;
    
    // 清除所有定时器
    this.updateTimers.forEach(timer => clearTimeout(timer));
    this.updateTimers.clear();
    
    logger.debug('脑波数据模拟器已停止');
  }

  /**
   * 获取模拟器状态
   * @returns {Object} 模拟器状态信息
   */
  getStatus() {
    return {
      isRunning: this.isRunning,
      patientCount: this.patients.size,
      patients: Array.from(this.patients.keys())
    };
  }
}

// 创建全局实例
const brainwaveSimulator = new BrainwaveSimulator();

export default brainwaveSimulator;